import React from "react";

/**
 * One section panel of the editor workspace.
 *
 * Every panel stays mounted; navigation only toggles the `hidden` attribute.
 * That is the whole point of the mount-all architecture (discovery risk R2):
 * an unmounted panel would drop the amenity inputs' state, and re-mounting it
 * would re-run any effect its fields carry.
 *
 * The root element deliberately carries NO display utility class. Tailwind's
 * preflight hides `[hidden]` with `display: none`, and a `flex`/`grid` class
 * on the same element would win over it and leave the panel visible. The
 * layout lives on the inner wrapper instead.
 *
 * `data-listing-editor-section` names the panel in the DOM so the form can
 * find its way back from an invalid control to the section holding it —
 * `control.closest("[data-listing-editor-section]")`. That is what keeps the
 * reveal in ListingEditorShell free of a field-to-section table: a control
 * added to any panel later is covered without touching the handler.
 */
export default function ListingEditorPanel({ id, title, description, active, children }) {
  const headingId = `listing-section-${id}-title`;

  return (
    <section
      id={`listing-section-${id}`}
      data-listing-editor-section={id}
      aria-labelledby={headingId}
      hidden={!active}
    >
      <h4 id={headingId} className="m-0 text-[15px] font-semibold text-foreground">
        {title}
      </h4>
      {description ? (
        <p className="mb-5 mt-1 text-[13px] text-muted-foreground">{description}</p>
      ) : (
        <div className="mb-5" />
      )}
      <div className="grid gap-5">{children}</div>
    </section>
  );
}
